import React from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, InputAdornment, List, ListItemButton, ListItemText,
  Stack, Switch, TextField, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { stargraveAdvancedWeapons } from '../../data/stargrave/advancedWeapons';

interface StargraveAdvancedWeaponModalProps {
  open: boolean;
  availableCredits: number;
  onClose: () => void;
  onAdd: (weaponId: string, addForFree: boolean) => void;
}

export const StargraveAdvancedWeaponModal: React.FC<StargraveAdvancedWeaponModalProps> = ({
  open,
  availableCredits,
  onClose,
  onAdd,
}) => {
  const [selectedId, setSelectedId] = React.useState('');
  const [filter, setFilter] = React.useState('');
  const [addForFree, setAddForFree] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const selected = stargraveAdvancedWeapons.find((weapon) => weapon.id === selectedId);
  const displayed = stargraveAdvancedWeapons
    .filter((weapon) => !filter.trim() || `${weapon.name} ${weapon.summary}`.toLowerCase().includes(filter.trim().toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  const reset = () => { setSelectedId(''); setFilter(''); setAddForFree(false); setError(null); };
  const handleClose = () => { reset(); onClose(); };
  const handleAdd = () => {
    if (!selected) return setError('Select an Advanced Weapon.');
    if (!addForFree && selected.cost > availableCredits) return setError('Not enough credits.');
    onAdd(selected.id, addForFree);
    handleClose();
  };

  return <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
    <DialogTitle>Add Advanced Weapon</DialogTitle>
    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      {error && <Alert severity="error">{error}</Alert>}
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1}>
        <Typography color="text.secondary">Available: {availableCredits}cr</Typography>
        <FormControlLabel control={<Switch checked={addForFree} onChange={(event) => setAddForFree(event.target.checked)} />} label="Add found weapon" />
      </Stack>
      <TextField size="small" value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Search Advanced Weapons"
        slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }} />
      <List dense sx={{ maxHeight: 340, overflowY: 'auto', border: 1, borderColor: 'divider' }}>
        {displayed.map((weapon) => <ListItemButton key={weapon.id} selected={selectedId === weapon.id}
          disabled={!addForFree && weapon.cost > availableCredits} onClick={() => { setSelectedId(weapon.id); setError(null); }}>
          <ListItemText primary={weapon.name} secondary={`Buy ${weapon.cost}cr | Sell ${weapon.salePrice}cr`} />
        </ListItemButton>)}
      </List>
      {selected && <Box>
        <Typography variant="h6">{selected.name}</Typography>
        <Typography variant="body2" sx={{ my: 0.5 }}>{selected.summary}</Typography>
        <Typography variant="caption" color="text.secondary">Replaces {selected.baseWeaponId} | {selected.gearSlots} gear slot{selected.gearSlots === 1 ? '' : 's'} | Core book p. 80</Typography>
      </Box>}
    </DialogContent>
    <DialogActions>
      <Button onClick={handleClose}>Cancel</Button>
      <Button variant="contained" onClick={handleAdd} disabled={!selected}>Add Weapon</Button>
    </DialogActions>
  </Dialog>;
};
